import type { ReactElement } from "react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { resolveTenantSlug } from "@/hooks/useTenantPath";
import { tenantPath as buildTenantPath } from "@/lib/urls";
import { useDemoV2 } from "@/features/demo/hooks/useDemoV2";
import { useSession } from "@/lib/hooks/useSession";

import NewOrderPage from "./NewOrder.V3";

export default function NewOrderPeek(): ReactElement {
  const { subdomain } = useParams<{ subdomain?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const demoContext = useDemoV2();
  const session = useSession();

  const fallbackSlug = useMemo(() => resolveTenantSlug(null), []);
  const [shouldShowMissingTenantNotice, setShouldShowMissingTenantNotice] = useState(false);
  const shouldForceDemo = !session.loading && !session.authenticated;

  useEffect(() => {
    if (session.loading) {
      return;
    }

    if (subdomain) {
      if (shouldShowMissingTenantNotice) {
        setShouldShowMissingTenantNotice(false);
      }
      return;
    }

    if (shouldForceDemo) {
      if (shouldShowMissingTenantNotice) {
        setShouldShowMissingTenantNotice(false);
      }
      return;
    }

    if (!fallbackSlug) {
      if (!shouldShowMissingTenantNotice) {
        setShouldShowMissingTenantNotice(true);
      }
      return;
    }

    const currentQuery = location.search ? location.search.replace(/^\?/, "") : "";
    const destination = `${buildTenantPath(fallbackSlug, "sales/new/peek")}${currentQuery ? `?${currentQuery}` : ""}`;

    if (destination !== `${location.pathname}${location.search}`) {
      navigate(destination, { replace: true });
    }
  }, [fallbackSlug, location.pathname, location.search, navigate, session.loading, shouldForceDemo, shouldShowMissingTenantNotice, subdomain]);

  useEffect(() => {
    if (!shouldForceDemo || session.loading || !subdomain) {
      return;
    }

    const currentQuery = location.search ? location.search.replace(/^\?/, "") : "";
    const destination = `/sales/new/peek${currentQuery ? `?${currentQuery}` : ""}`;

    if (destination !== `${location.pathname}${location.search}`) {
      navigate(destination, { replace: true });
    }
  }, [location.pathname, location.search, navigate, session.loading, shouldForceDemo, subdomain]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let updated = false;

    if (params.get("view") !== "peek") {
      params.set("view", "peek");
      updated = true;
    }

    if (!params.get("source")) {
      params.set("source", "counter");
      updated = true;
    }

    const wantsDemoMode = demoContext.isDemo || shouldForceDemo;

    if (wantsDemoMode) {
      if (params.get("mode") !== "demo") {
        params.set("mode", "demo");
        updated = true;
      }
    } else if (params.has("mode")) {
      params.delete("mode");
      updated = true;
    }

    if (!updated) {
      return;
    }

    const nextQuery = params.toString();
    navigate(`${location.pathname}${nextQuery ? `?${nextQuery}` : ""}`, { replace: true });
  }, [demoContext.isDemo, location.pathname, location.search, navigate, shouldForceDemo]);

  if (!subdomain && !shouldForceDemo && shouldShowMissingTenantNotice) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-6 text-center text-sm text-muted-foreground">
        <p className="max-w-md">
          We could not resolve the workspace for this counter peek. Re-open the counter from your
          dashboard or ensure you are logged into the correct location, then try again.
        </p>
      </div>
    );
  }

  return <NewOrderPage />;
}
